import type React from "react";
import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import { setOrgTimezone } from "simplydo/core/dates";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import actions from "actions";
import api from "api";
import { useAppDispatch } from "store";
import util from "utils/utils";

import AppStyledDashboard from "./AppStyledDashboard";
import { Footer } from "./lib/UI";
import { SimpleNavBar } from "./lib/UI/NavBar";

export const StyledDashboardContainer = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

type StyledDashboardProps = {
  content: React.ReactNode;
  hideName?: boolean;
  fluid?: boolean;
  excludeWelcomeText?: boolean;
  scrolling?: boolean;
  padded?: boolean | "very";
  backgroundColor?: string;
};

const AppWrap = ({
  content,
  hideName,
  fluid,
  excludeWelcomeText,
  scrolling = true,
}: StyledDashboardProps) => {
  const theme = useTheme();

  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // Fetch initial organisation by code if present in the URL
  useEffect(() => {
    const code = util.code();
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get("lang");
    i18n.changeLanguage(lang || "en");
    if (code) {
      api.organisations.getByCode(
        code,
        (newOrganisation) => {
          i18n.changeLanguage(lang || newOrganisation.defaultLanguage || "en");
          setOrgTimezone(newOrganisation.defaultTimezone);
          dispatch(
            actions.organisations.receiveUrlOrganisation(newOrganisation),
          );
        },
        () => {},
      );
    } else {
      api.organisations.getByDomain(
        window.location.hostname,
        (newOrganisation) => {
          if (!newOrganisation) {
            return;
          }
          i18n.changeLanguage(lang || newOrganisation.defaultLanguage || "en");
          setOrgTimezone(newOrganisation.defaultTimezone);
          dispatch(
            actions.organisations.receiveUrlOrganisation(newOrganisation),
          );
        },
        () => {},
      );
    }
  }, [dispatch, i18n]);

  return (
    <StyledDashboardContainer>
      <SimpleNavBar />
      <AppStyledDashboard
        content={content}
        hideName={!!hideName}
        fluid={!!fluid}
        excludeWelcomeText={!!excludeWelcomeText}
        scrolling={scrolling}
      />
      {!theme.sizes.isMobile ? <Footer /> : null}
    </StyledDashboardContainer>
  );
};

export default AppWrap;
